import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDistributorInfo } from "../../redux-toolkit/user/user-thunks";
import { Redirect, Route } from "react-router-dom";
import {
  UserOutlined,
  LockOutlined,
  ShoppingOutlined,
  AppstoreAddOutlined,
  UnorderedListOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  HomeOutlined,
} from "@ant-design/icons";

import ContentTitle from "../../components/ContentTitle/ContentTitle";
import AccountLink from "./AccountLink/AccountLink";
import PersonalData from "./PersonalData/PersonalData";
import AddPerfume from "./AddPerfume/AddPerfume";
import PerfumeList from "./PerfumeList/PerfumeList";
import EditPerfume from "./EditPerfume/EditPerfume";
import ManageHome from "./ManageHome/ManageHome";
import OrdersList from "./OrdersList/OrdersList";
import ManageUserOrder from "./ManageUserOrder/ManageUserOrder";
import UsersList from "./UsersList/UsersList";
import ManageUser from "./ManageUser/ManageUser";
import ChangePassword from "./ChangePassword/ChangePassword";
import PersonalOrdersList from "./PersonalOrdersList/PersonalOrdersList";
import AddPincode from "./AddPincode/AddPincode";
import AddDistributor from "./Dist/AddDistributor";
import MyStock from "./Dist/MyStock";
import AddME from "./ME/AddME";
import AddEmp from "./ME/AddEmp";
import MyAddresses from "./ManageUser/MyAddresses";
import InvoicePage from "./OrdersList/InvoicePage";

import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { resetAuthState } from "../../redux-toolkit/auth/auth-slice";
import { fetchUserInfo } from "../../redux-toolkit/user/user-thunks";
import { UserRoles } from "../../types/types";
import {
  ACCOUNT,
  ACCOUNT_ADMIN_ADD,
  ACCOUNT_ADMIN_ORDERS,
  ACCOUNT_ADMIN_HOME,
  ACCOUNT_ADMIN_PERFUMES,
  ACCOUNT_ADMIN_USERS,
  ACCOUNT_USER_EDIT,
  ACCOUNT_USER_INFO,
  ACCOUNT_USER_ORDERS,
  ACCOUNT_ADMIN_PINCODES,
  ACCOUNT_ADMIN_ADD_DIST,
  ACCOUNT_ADMIN_ADD_ME,
  ACCOUNT_ADMIN_ADD_ADDRESS,
  ACCOUNT_ADMIN_DIST_ORDERS,
  ACCOUNT_ADMIN_ADD_EMP,
  ACCOUNT_ADMIN_DIST_STOCK,
} from "../../constants/routeConstants";

import "./Account.css";

const Account: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const usersData = useSelector(selectUserFromUserState);

  const [isAdmin, setIsAdmin]           = useState(false);
  const [isDistributor, setIsDistributor] = useState(false);
  const [isME, setIsME]                 = useState(false);
  const [isEmployee, setIsEmployee]     = useState(false);

  useEffect(() => {
    dispatch(resetAuthState());
    const role   = localStorage.getItem("role");
    const distId = localStorage.getItem("distId");

    if (role === "3") {
      dispatch(fetchDistributorInfo(distId!));
      setIsDistributor(true);
    } else if (role === "2") {
      setIsME(true);
    } else if (role === "4") {
      setIsEmployee(true);
    } else {
      dispatch(fetchUserInfo());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isDistributor && usersData) {
      setIsAdmin(usersData.roles?.[0] === UserRoles.ADMIN);
    }
  }, [usersData, isDistributor]);

  /* ── Sidebar link sets ─────────────────────────────────── */
  const AdminLinks = (
    <>
      <AccountLink link={ACCOUNT_USER_INFO}      title="Personal Data"              icon={<UserOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD}       title="Add Product"                icon={<AppstoreAddOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_PERFUMES}  title="List of Products"           icon={<UnorderedListOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ORDERS}    title="All Orders"                 icon={<ShoppingOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_USERS}     title="All Users"                  icon={<TeamOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_HOME}      title="Manage Home Page"           icon={<HomeOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_PINCODES}  title="Manage System"            icon={<EnvironmentOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD_DIST}  title="Manage Distributors"        icon={<EnvironmentOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD_ME}    title="Manage Marketing Executives" icon={<EnvironmentOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD_EMP}   title="Manage Employee"            icon={<EnvironmentOutlined />} />
    </>
  );

  const DistributorLinks = (
    <>
      <AccountLink link={ACCOUNT_USER_INFO}         title="Profile Data"     icon={<UserOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_DIST_ORDERS} title="All User Orders"  icon={<ShoppingOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_USERS}       title="All Users"        icon={<TeamOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD_ADDRESS} title="My Address"       icon={<EnvironmentOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_DIST_STOCK} title="Distributor Stock"  icon={<ShoppingOutlined />} />
    </>
  );

  const EmployeeLinks = (
    <>
      <AccountLink link={ACCOUNT_USER_INFO}      title="Personal Data"    icon={<UserOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD}       title="Add Product"      icon={<AppstoreAddOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_PERFUMES}  title="List of Products" icon={<UnorderedListOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ORDERS}    title="All Orders"       icon={<ShoppingOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_USERS}     title="All Users"        icon={<TeamOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_HOME}      title="Manage Home Page" icon={<HomeOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_PINCODES}  title="Manage System"  icon={<EnvironmentOutlined />} />
    </>
  );

  const MELinks = (
    <>
      <AccountLink link={ACCOUNT_USER_INFO}         title="Profile Data"         icon={<UserOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_DIST_ORDERS} title="All User Orders"      icon={<ShoppingOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_USERS}       title="All Users"            icon={<TeamOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD_DIST}    title="Manage Distributors"  icon={<EnvironmentOutlined />} />
    </>
  );

  const CustomerLinks = (
    <>
      <AccountLink link={ACCOUNT_USER_INFO}         title="Personal Data"    icon={<UserOutlined />} />
      <AccountLink link={ACCOUNT_USER_EDIT}         title="Change Password"  icon={<LockOutlined />} />
      <AccountLink link={ACCOUNT_USER_ORDERS}       title="My Orders"        icon={<ShoppingOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_ADD_ADDRESS} title="My Address"       icon={<EnvironmentOutlined />} />
      <AccountLink link={ACCOUNT_ADMIN_DIST_STOCK} title="Distributor Stock"       icon={<EnvironmentOutlined />} />
      </>
  );

  const SidebarLinks =
    isAdmin       ? AdminLinks       :
    isDistributor ? DistributorLinks :
    isEmployee    ? EmployeeLinks    :
    isME          ? MELinks          :
                    CustomerLinks;

  return (
    <div className="acc-page">
      <div className="acc-layout">

        {/* ── Sidebar (desktop only) ── */}
        <aside className="acc-sidebar">
          <div className="acc-sidebar-inner">
            <div className="acc-sidebar-header">
              <ContentTitle title="My Account" titleLevel={4} icon={<UserOutlined />} />
            </div>
            <nav className="acc-nav">
              {SidebarLinks}
            </nav>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="acc-main">
          <Route exact path={`${ACCOUNT_USER_ORDERS}/:id`} component={ManageUserOrder} />
          <Route exact path={`${ACCOUNT_ADMIN_USERS}/:id`} component={ManageUser} />
          <Route exact path={ACCOUNT_ADMIN_PINCODES}       component={AddPincode} />
          <Route exact path={ACCOUNT_ADMIN_ADD_DIST}       component={AddDistributor} />
          <Route exact path={ACCOUNT_ADMIN_ADD_ME}         component={AddME} />
          <Route exact path={ACCOUNT_ADMIN_ADD_EMP}        component={AddEmp} />
          <Route exact path={`${ACCOUNT_USER_ORDERS}/:id/invoice`} component={InvoicePage} />
          <Route
            exact
            path={[
              ACCOUNT_USER_INFO,
              ACCOUNT_USER_EDIT,
              ACCOUNT_USER_ORDERS,
              ACCOUNT_ADMIN_ADD,
              ACCOUNT_ADMIN_PERFUMES,
              ACCOUNT_ADMIN_ORDERS,
              ACCOUNT_ADMIN_DIST_ORDERS,
              ACCOUNT_ADMIN_USERS,
              ACCOUNT_ADMIN_HOME,
              ACCOUNT_ADMIN_DIST_STOCK,
              ACCOUNT_ADMIN_ADD_ADDRESS,
            ]}
            render={() => (
              <div className="acc-content-card">
                <Route path={ACCOUNT_USER_INFO}  component={PersonalData} />
                <Route path={ACCOUNT_USER_EDIT}  component={ChangePassword} />
                <Route exact path={ACCOUNT_USER_ORDERS}       component={PersonalOrdersList} />
                <Route exact path={ACCOUNT_ADMIN_ADD_ADDRESS} component={MyAddresses} />
                <Route exact path={ACCOUNT_ADMIN_DIST_STOCK}        component={MyStock} />

                {isAdmin && (
                  <>
                    <Route path={ACCOUNT_ADMIN_ADD}                        component={AddPerfume} />
                    <Route exact path={ACCOUNT_ADMIN_PERFUMES}             component={PerfumeList} />
                    <Route exact path={`${ACCOUNT_ADMIN_PERFUMES}/:id`}   component={EditPerfume} />
                    <Route exact path={ACCOUNT_ADMIN_ORDERS}               component={OrdersList} />
                    <Route exact path={ACCOUNT_ADMIN_USERS}                component={UsersList} />
                    <Route exact path={ACCOUNT_ADMIN_HOME}                 component={ManageHome} />
                  </>
                )}

                {isDistributor && (
                  <>
                    <Route exact path={ACCOUNT_ADMIN_DIST_ORDERS} component={OrdersList} />
                    <Route exact path={ACCOUNT_ADMIN_USERS}        component={UsersList} />
                    <Route exact path={ACCOUNT_ADMIN_DIST_STOCK}        component={MyStock} />
                  </>
                )}
              </div>
            )}
          />

          <Route exact path={ACCOUNT}>
            {isAdmin ? (
              <Redirect to={ACCOUNT_ADMIN_PERFUMES} />
            ) : (
              <Redirect to={ACCOUNT_USER_INFO} />
            )}
          </Route>
        </main>
      </div>
    </div>
  );
};

export default Account;